import { useFormik } from 'formik';
import { ChangeEvent, useMemo } from 'react';
import { useQuery } from 'react-query';
import { NavigateFunction, useLocation, useNavigate } from 'react-router-dom';

import { Button, Form, InputField, Loader } from '@/components';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import useToast from '@/hooks/useToast';
import ROUTES from '@/router/routes';
import { getDatosBancarios, postDatosBancarios } from '@/services/tda';
import { SelectedScreen } from '@/types/editProfile';
import { DatosBancariosBody } from '@/types/tda';
import {
  myCbuSchema,
  navigateBasePath403,
  TEXTS,
  validateFieldError,
} from '@/utils';

type FormValues = {
  nameAndSurName?: string;
  cuit?: number;
  bank: string;
  acountNumber?: string;
  cbu?: string;
};

const MyCbu = () => {
  const toast = useToast();
  const navigate: NavigateFunction = useNavigate();
  const location = useLocation();
  const locationState: SelectedScreen = location.state;
  const { isMobile } = useIsDesktop();

  const { data: dataInit, isLoading } = useQuery(
    'initialDataCbu',
    async () => await getDatosBancarios(),
    {
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        if ('codError' in data && 'mensaje' in data) {
          toast.show(data.mensaje as string);
          navigateBasePath403(navigate);
          // navigate(ROUTES.basePath, { replace: true });
        }
      },
      onError: (error) => {
        const err = error as Error;
        toast.showError(err);
        navigate(ROUTES.notFound, { replace: true });
      },
    }
  );

  const initialValues = useMemo(() => {
    return {
      nameAndSurName: dataInit?.db?.nombreTitular,
      cuit: dataInit?.db?.cuit ? Number(dataInit.db.cuit) : undefined,
      bank: dataInit?.db?.banco ?? '',
      acountNumber: dataInit?.db?.nroCuenta,
      cbu: dataInit?.db?.cbu,
    };
  }, [dataInit]);

  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues,
    validateOnBlur: true,
    validationSchema: myCbuSchema,
    onSubmit: async (values, { setSubmitting, setFieldError }) => {
      const formData = {
        nombreTitular: values.nameAndSurName ?? '',
        cuit: values.cuit?.toString() ?? '',
        nroCuenta: values.acountNumber ?? '',
        banco: values.bank ?? '',
        cbu: values.cbu?.toString() ?? '',
      } satisfies DatosBancariosBody;

      try {
        const response = await postDatosBancarios(formData);
        if (response) {
          if (response.estado) {
            toast.show(TEXTS.personalDataUpdated);

            if (locationState.returnPage) {
              navigate(locationState.returnPage, {
                state: { showtourneys: true },
              });
            }
          } else {
            toast.show(response.mensaje);

            if (
              response.estado === false &&
              response.codError === 3314 &&
              response.campo === 'nombreTitular'
            ) {
              setFieldError('nameAndSurName', response.mensaje);
            }

            if (
              response.estado === false &&
              response.codError === 3314 &&
              response.campo === 'cuit'
            ) {
              setFieldError('cuit', response.mensaje);
            }
            if (response.estado === false && response.codError === 3311) {
              setFieldError('cbu', response.mensaje);
            }
            if (response.estado === false && response.codError === 3313) {
              setFieldError('acountNumber', response.mensaje);
            }
          }
        }
      } catch (error) {
        toast.show(TEXTS.callCenterError);
      }
      setSubmitting(false);
    },
  });

  const {
    values,
    handleSubmit,
    isSubmitting,
    handleBlur,
    handleChange,
    errors,
    touched,
    dirty,
  } = formik;

  const handleCbuChange = (
    event: ChangeEvent<HTMLInputElement>,
    onChange: any
  ) => {
    const { value } = event.target;
    const cleanedValue = value.replace(/[^0-9]/g, '');
    event.target.value = cleanedValue;
    onChange(event);
  };

  // useEffect(() => {
  //   if (locationState.selectedScreen === 'MyCBU') {
  //     // Give time for the component to fully render before scrolling
  //     setTimeout(() => {
  //       window.scrollTo({
  //         top: 0,
  //         behavior: 'auto', // Changed from 'smooth' to 'auto'
  //       });

  //       // Make sure body/html is scrollable
  //       document.body.style.overflow = 'auto';
  //       document.documentElement.style.overflow = 'auto';
  //     }, 100);
  //   }
  // }, [locationState]);

  return (
    // <EditMyAccountLayout goBackTo={ROUTES.editAccount.menu} title="Mi CBU">
    <>
      {<Loader isVisible={isLoading || isSubmitting} timeout={isSubmitting} />}
      <div className="lg:ml-[115px] p-4 lg:p-0 lg:mt-10 lg:pr-[40px] pb-[90px] overflow-auto">
        <Form.Form
          className="space-y-[1.75rem] overflow-visible"
          loading={isSubmitting}
          onSubmit={handleSubmit}
        >
          <p className="font-medium">
            Estos datos son necesarios, ya que si sos el ganador de ciertas
            competencias recibirás el premio por medio de una transferencia
            bancaria.
          </p>
          <p className="text-lg ">Mi CBU</p>
          <div className="flex flex-col gap-y-4  lg:flex-row lg:gap-8 lg:gap-y-0">
            <Form.Field>
              <InputField
                onlyLetters
                behavior="default"
                error={validateFieldError(
                  touched.nameAndSurName,
                  errors.nameAndSurName
                )}
                label="Nombre y apellido del titular de la cuenta"
                maxLength={30}
                name="nameAndSurName"
                placeholder="Ingresar"
                value={values.nameAndSurName ?? ''}
                onBlur={handleBlur}
                onChange={handleChange}
              />
            </Form.Field>
            <Form.Field>
              <InputField
                behavior="default"
                error={validateFieldError(touched.cuit, errors.cuit)}
                label="CUIT"
                maxLength={11}
                name="cuit"
                placeholder="Ingresar"
                value={values.cuit !== undefined ? values.cuit.toString() : ''}
                onBlur={handleBlur}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  handleCbuChange(event, handleChange)
                }
              />
            </Form.Field>
          </div>
          <div className="flex flex-col gap-y-4  lg:flex-row lg:gap-8 lg:gap-y-0">
            <Form.Field>
              <InputField
                behavior="default"
                error={validateFieldError(
                  touched.acountNumber,
                  errors.acountNumber
                )}
                label="Número de cuenta"
                maxLength={50}
                name="acountNumber"
                placeholder="Ingresar"
                value={values.acountNumber ?? ''}
                onBlur={handleBlur}
                onChange={handleChange}
              />
            </Form.Field>
            <Form.Field>
              <InputField
                behavior="default"
                error={validateFieldError(touched.bank, errors.bank)}
                label="Banco"
                maxLength={50}
                name="bank"
                placeholder="Ingresar"
                value={values.bank ?? ''}
                onBlur={handleBlur}
                onChange={handleChange}
              />
            </Form.Field>
          </div>
          <div className="flex flex-col gap-y-4  lg:flex-row lg:gap-8 lg:gap-y-0">
            <Form.Field>
              <InputField
                behavior="default"
                error={validateFieldError(touched.cbu, errors.cbu)}
                label="CBU"
                maxLength={22}
                name="cbu"
                placeholder="Ingresar"
                value={values.cbu ?? ''}
                onBlur={handleBlur}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  handleCbuChange(event, handleChange)
                }
              />
            </Form.Field>
          </div>
          <div className="flex flex-col gap-y-4  lg:flex-row lg:gap-8 lg:gap-y-0">
            {isMobile ? (
              <div className="fixed bottom-0 left-0 right-0 bg-white p-4 z-50">
                <Button
                  className="w-full py-2 px-4"
                  disabled={!dirty || isSubmitting}
                  type="submit"
                  variant={'primary'}
                >
                  Guardar cambios
                </Button>
              </div>
            ) : (
              <Button
                className="mb-4 max-w-full"
                disabled={!dirty || isSubmitting}
                type="submit"
                variant={'primary'}
              >
                Guardar cambios
              </Button>
            )}
          </div>
        </Form.Form>
      </div>
    </>
  );
};

export default MyCbu;
